import { load as loadRecaptcha } from "recaptcha-v3";

import Api from "@/components/Api";

import "./style.css";

export default class Form {
  constructor(form, options) {
    this.form = form;
    this.formErrorContainer = this.form.querySelector(".form__error");
    this.submitButton = this.form.querySelector(".form__submit");
    this.submitButtonLabel =
      this.submitButton.textContent || this.submitButton.innerText;

    this.configure(options);

    // get data
    this.action = this.form.action;
    this.method = this.form.method;
    this.isAjax =
      this.form.dataset.ajax !== undefined &&
      this.form.dataset.ajax.toLowerCase() !== "false";
    this.apiAction = this.form.dataset.apiAction;

    this.recaptchaSiteKey = this.form.dataset.recaptcha;
    this.recaptchaResponseInput = this.form.querySelector(
      "input[name=recaptchaResponse]"
    );
    this.isRecaptcha = !!this.recaptchaSiteKey && !!this.recaptchaResponseInput;

    this.fields = [
      ...this.form.querySelectorAll(
        "input:not([type=checkbox]):not([type=radio]):not(.novalidate), textarea, select, .checkboxes, .radio-buttons"
      ),
    ];

    this.inputs = [...this.form.querySelectorAll("input, textarea, select")];
    this.inputs.forEach(this.setupInput);

    this.form.addEventListener("submit", this.handleSubmit);
  }

  setupInput = (input) => {
    if(input.type == "checkbox") {
      input.addEventListener("click", this.handleInputBlur);
    } else {
      input.addEventListener("focus", this.handleInputFocus);
      input.addEventListener("blur", this.handleInputBlur);
    }
  };

  collectData = () => {
    const data = {};

    this.inputs.forEach((input) => {
      if (!input.name) {
        return;
      }

      if (input.type === "radio") {
        if (input.checked) {
          data[input.name] = input.value;
        } else if (!data.hasOwnProperty(input.name)) {
          data[input.name] = null;
        }
      } else if (input.type === "checkbox") {
        if (input.classList.contains("boolean")) {
          // it's a boolean checkbox, result will be true/false and it's an
          // unique input with this name
          data[input.name] = !!input.checked;
        } else {
          // classic use, it will be an array of values name ends with `[]`
          const isArray = input.name.endsWith("[]");
          const dataKey = isArray
            ? input.name.substr(0, input.name.length - 2)
            : input.name;

          if (input.checked) {
            if (!data.hasOwnProperty(dataKey)) {
              data[dataKey] = isArray ? [input.value] : input.value;
            } else if (isArray) {
              data[dataKey].push(input.value);
            } else {
              data[dataKey] = input.value;
            }
          } else if (!data.hasOwnProperty(dataKey)) {
            data[dataKey] = isArray ? [] : null;
          }
        }
      } else if (input.classList.contains("boolean")) {
        // it's boolean, result will be true/false
        data[input.name] = !!input.value;
      } else if(input.tagName.toLowerCase() === "select" && input.multiple) {
        const isArray = input.name.endsWith("[]");
        const dataKey = isArray
          ? input.name.substr(0, input.name.length - 2)
          : input.name;

        data[dataKey] = Array.from(input.selectedOptions).map(v=>v.value);
        console.log(data[dataKey]);
      } else {
        data[input.name] = input.value;
      }
    });

    return data;
  };

  handleInputFocus = (e) => {
    // e.currentTarget
    //   .closest(".form__group")
    //   .classList.remove("form__group--wrong");
  };
  handleInputBlur = (e) => {
    let field = e.currentTarget;

    if (field.classList.contains("checkboxes__option-input")) {
      field = field.closest(".checkboxes");
    } else if (field.classList.contains("radio-buttons__option-input")) {
      field = field.closest(".radio-buttons");
    }

    if(!field.classList.contains('novalidate')) {
      this.validate(field);  
    }
  };

  handleSubmit = async (e) => {
    this.dismissFormError();

    const isFormValid = this.validate();

    if (!isFormValid) {
      e.preventDefault();

      this.form.classList.add("form--wrong");
      if (this.formErrorContainer) {
        this.formErrorContainer.innerText = window.wpData.copy.ERROR_CHECK_FIELDS;
      }

      const firstWrongField = this.form.querySelector(".form__group--wrong");

      if (firstWrongField) {
        firstWrongField.focus();

        const yOffset = -132 - 20;
        const y =
          firstWrongField.getBoundingClientRect().top +
          window.pageYOffset +
          yOffset;

        window.scrollTo({ top: y, behavior: "smooth" });
      }

      return;
    }

    this.submitButtonLabel =
      this.submitButton.textContent || this.submitButton.innerText;

    if (!this.isAjax) {
      return;
    }

    e.preventDefault();

    if (!Api.hasOwnProperty(this.apiAction)) {
      console.log("The defined api doesn't exist.");
      return;
    }

    this.submitButton.disabled = true;
    this.submitButton.innerText = this.submitLoadingLabel;
    this.submitButton.classList.add("button--loading");

    const succeededEvent = new CustomEvent("submitsent");
    this.form.dispatchEvent(succeededEvent);

    if (this.isRecaptcha) {
      const recaptcha = await loadRecaptcha(this.recaptchaSiteKey, {
        useRecaptchaNet: true,
        autoHideBadge: true,
      });
      const token = await recaptcha.execute("submit");
      this.recaptchaResponseInput.value = token;
    }

    const data = this.collectData();

    try {
      const response = await Api[this.apiAction](data);

      if (!response) {
        // Something went wrong with this request, no response
        throw new Error();
      }

      // trigger event for submition succeeded
      const succeededEvent = new CustomEvent("submitsucceeded");
      this.form.dispatchEvent(succeededEvent);

      await this.onSubmitSucceeded(response);

      if (!this.gainControlOnSubmitSucceeded) {
        this.submitButton.disabled = false;
        this.submitButton.classList.add('button--success')
        this.submitButton.classList.remove("button--loading");
        this.submitButton.innerText = this.submitSuccessLabel;

        if (this.resetAfterSubmit) {
          this.form.reset();
        }
      }
    } catch (error) {
      console.log("Error from api", error);

      this.showFormError(error);

      // trigger event for submition failed
      const failedEvent = new CustomEvent("submitfailed");
      this.form.dispatchEvent(failedEvent);

      await this.onSubmitFailed(error);

      this.submitButton.disabled = false;
      this.submitButton.classList.remove("button--loading");
      this.submitButton.innerText = this.submitButtonLabel;
    }
  };

  showFormError(error) {
    this.form.classList.add("form--wrong");

    if (!this.formErrorContainer) {
      return;
    }

    const message =
      error?.response?.message !== false
        ? error?.response?.message ?? window.wpData.copy.ERROR_GENERIC
        : false;

    if (message) {
      this.formErrorContainer.innerText = message;
    } else {
      this.formErrorContainer.innerText = "";
    }

    const validationErrors = error?.validation ?? [];
    validationErrors.forEach((e) => {
      const input = this.inputs.find((i) => i.name === e.field);
      const formGroup = input.closest(".form__group");
      formGroup.classList.add("form__group--wrong");

      const inputErrorContainer = formGroup.querySelector(".form__group-error");

      if (inputErrorContainer) {
        inputErrorContainer.innerText = e.message;
      }
    });
  }

  dismissFormError() {
    this.form.classList.remove("form--wrong");
  }

  isValid = (page) => {
    let isThereAnyError = false;
    
    this.fields.filter(field => {
      if(page === undefined) {
        return true;
      } else {
        return field.closest('.form__step').dataset.index == page
      }
    }).forEach((field) => {
      if (field.classList.contains("checkboxes")) {
        const checkboxes = [
          ...field.querySelectorAll(".checkboxes__option-input"),
        ];

        const requiredChecks = parseInt(field.dataset.required, 10) || 0;

        let checks = 0;
        let isThereAnyCheckboxError = false;

        checkboxes.forEach((checkbox) => {
          if (checkbox.checked) {
            checks++;
          } else if (checkbox.required) {
            isThereAnyCheckboxError = true;
          }
        });

        if (checks < requiredChecks) {
          isThereAnyCheckboxError = true;
        }

        if(isThereAnyCheckboxError) {
          isThereAnyError = true;
        }

        return;
      }

      if (field.classList.contains("radio-buttons")) {
        const radioButtons = [
          ...field.querySelectorAll(".radio-buttons__option-input"),
        ];

        radioButtons.forEach((radioButton) => {});

        return;
      }

      if (field.type === "hidden") {
        return;
      }

      if (!field.validity.valid) {
        isThereAnyError = true;
      }
    });

    return !isThereAnyError;    
  }

  validate = (field) => {
    this.submitButton.classList.remove('button--success');
    this.submitButton.innerText = this.submitButtonLabel;
    const fields = field ? [field] : this.fields;

    let isThereAnyError = false;
    
    fields.forEach((field) => {
      if (field.classList.contains("checkboxes")) {
        const checkboxes = [
          ...field.querySelectorAll(".checkboxes__option-input"),
        ];

        const requiredChecks = parseInt(field.dataset.required, 10) || 0;

        let checks = 0;
        let isThereAnyCheckboxError = false;

        checkboxes.forEach((checkbox) => {
          if (checkbox.checked) {
            checks++;
          } else if (checkbox.required) {
            isThereAnyCheckboxError = true;
          }
        });

        if (checks < requiredChecks) {
          isThereAnyCheckboxError = true;
        }

        const formGroup = field.closest(".form__group");

        const errorField = formGroup.querySelector(".form__group-error");

        if (isThereAnyCheckboxError) {
          formGroup.classList.add("form__group--wrong");
          isThereAnyError = true;

          if (errorField) {
            if (checks < requiredChecks) {
              errorField.innerText =
                requiredChecks === 1
                  ? window.wpData.copy.ERROR_FIELD_CHECKBOXS_ONE_REQUIRED
                  : window.wpData.copy.ERROR_FIELD_CHECKBOXS_MULTI_REQUIRED.replace(
                      "%d",
                      requiredChecks
                    );
            } else {
              errorField.innerText =
                checkboxes.length > 1
                  ? window.wpData.copy.ERROR_FIELD_CHECKBOXS_REQUIRED
                  : window.wpData.copy.ERROR_FIELD_CHECKBOX_REQUIRED;
            }
          }
        } else {
          formGroup.classList.remove("form__group--wrong");
          if (errorField) {
            errorField.innerText = "";
          }
        }

        return;
      }

      if (field.classList.contains("radio-buttons")) {
        const radioButtons = [
          ...field.querySelectorAll(".radio-buttons__option-input"),
        ];

        radioButtons.forEach((radioButton) => {});

        return;
      }

      if (field.type === "hidden") {
        return;
      }

      if (!field.validity.valid) {
        isThereAnyError = true;
        field.closest(".form__group").classList.add("form__group--wrong");
        field.closest(".form__group").classList.remove("form__group--success");
      } else {
        field.closest(".form__group").classList.remove("form__group--wrong");
        field.closest(".form__group").classList.add("form__group--success");  
      }

      const errorField = field
        .closest(".form__group")
        .querySelector(".form__group-error");

      if (errorField) {
        if (field.validity.valueMissing) {
          errorField.innerText =
            field.type === "checkbox"
              ? window.wpData.copy.ERROR_FIELD_CHECKBOX_REQUIRED
              : window.wpData.copy.ERROR_FIELD_REQUIRED;
        } else if (field.validity.tooLong) {
          errorField.innerText = window.wpData.copy.ERROR_FIELD_TOO_LONG;
        } else if (field.validity.tooShort) {
          errorField.innerText = window.wpData.copy.ERROR_FIELD_TOO_SHORT;
        } else if (field.validity.badInput || field.validity.typeMismatch) {
          errorField.innerText = window.wpData.copy.ERROR_FIELD_NOT_VALID;
        } else {
          errorField.innerText = "";
        }
      }
    });

    let valid = false;
    if (isThereAnyError) {
      this.form.classList.add("form--wrong");
    } else if(!field || this.form.querySelectorAll('.form__group--wrong').length === 0) {
      this.form.classList.remove("form--wrong");
      valid = true;
    }

    var event = new CustomEvent("formValidated", {
      detail: {
        valid
      }
    });
    this.form.dispatchEvent(event);

    return !isThereAnyError;
  };

  configure({
    submitLoadingLabel = 'Loading',
    submitSuccessLabel = null,
    onSubmitSucceeded = (f) => f,
    onSubmitFailed = (f) => f,
    resetAfterSubmit = true,
    gainControlOnSubmitSucceeded = false,
  }) {
    this.submitLoadingLabel = submitLoadingLabel;
    this.submitSuccessLabel = submitSuccessLabel || this.submitButtonLabel;
    this.onSubmitSucceeded = onSubmitSucceeded;
    this.onSubmitFailed = onSubmitFailed;
    this.resetAfterSubmit = resetAfterSubmit;
    this.gainControlOnSubmitSucceeded = gainControlOnSubmitSucceeded;
  }

  onDestroy = () => {
    this.inputs.forEach((input) => {
      if(input.type == "checkbox") {
        input.removeEventListener("click", this.handleInputBlur);
      } else {
        input.removeEventListener("focus", this.handleInputFocus);
        input.removeEventListener("blur", this.handleInputBlur);
      }
    });

    this.form.removeEventListener("submit", this.handleSubmit);
  };
}
